import { Suspense, lazy, useEffect, useState } from 'react';

import { Outlet, Route, useLocation } from 'react-router-dom';

import { MotionConfig } from 'motion/react';

import { AnalyticsIdentity, ExperienceAnalytics, RouteAnalytics } from '@cctv/analytics';
import { RouteWink, TopNav } from '@cctv/components';
import { ExperienceProvider } from '@cctv/contexts/ExperienceContext';
import { UserProvider } from '@cctv/contexts/UserContext';
import { FeedbackExperienceBridge, FeedbackProvider } from '@cctv/feedback';
import Calendar from '@cctv/pages/Calendar/Calendar';
import Create from '@cctv/pages/Create/Create';
import CreateEvent from '@cctv/pages/Events/CreateEvent';
import EditEvent from '@cctv/pages/Events/EditEvent';
import EventDetail from '@cctv/pages/Events/EventDetail';
import Avatar from '@cctv/pages/Experience/Avatar';
import Experience from '@cctv/pages/Experience/Experience';
import ExperiencesList from '@cctv/pages/Experiences/ExperiencesList';
import Monitor from '@cctv/pages/Monitor/Monitor';
import CreatePerformer from '@cctv/pages/Performers/CreatePerformer';
import EditPerformer from '@cctv/pages/Performers/EditPerformer';
import PerformerProfile from '@cctv/pages/Performers/PerformerProfile';
import PerformersList from '@cctv/pages/Performers/PerformersList';
import Playbill from '@cctv/pages/Playbill/Playbill';
import Register from '@cctv/pages/Register';
import About from '@cctv/pages/about';
import Home from '@cctv/pages/home';
import Join from '@cctv/pages/join';
import Stylesheet from '@cctv/pages/stylesheet';

import {
  AllowRegisterRoute,
  RequireAdmin,
  RequireAuth,
  RequireExperienceHostOrAdmin,
  RequireExperienceParticipantOrAdmin,
} from './RouteRules';

import styles from './App.module.scss';

const BlockPage = lazy(() =>
  import('@cctv/pages/Block/Block').then((m) => ({ default: m.BlockPage })),
);
const ManageCreateBlock = lazy(() => import('@cctv/pages/ManageCreateBlock/ManageCreateBlock'));
const ManageViewer = lazy(() => import('@cctv/pages/Manage/Viewer/ManageViewer'));
const FocusMode = lazy(() => import('@cctv/pages/Manage/Focus/FocusMode'));
const ManageParticipantsPage = lazy(
  () => import('@cctv/pages/Manage/Subpages/ManageParticipantsPage'),
);
const ManagePlaybillPage = lazy(() => import('@cctv/pages/Manage/Subpages/ManagePlaybillPage'));
const ManageDebugPage = lazy(() => import('@cctv/pages/Manage/Subpages/ManageDebugPage'));
const Timeline = lazy(() => import('@cctv/pages/Timeline/Timeline'));

function App() {
  const [booting, setBooting] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setBooting(false), 900);
    return () => clearTimeout(t);
  }, []);

  const currentRoute = useLocation();

  return (
    <UserProvider>
      <AnalyticsIdentity />
      <RouteAnalytics />
      {/* Above the router so opening feedback never unmounts the experience. */}
      <FeedbackProvider>
        <MotionConfig reducedMotion="user">
          <div className={`app${booting ? ' app--booting' : ''}`}>
            {!currentRoute.pathname.includes('/monitor') &&
              !currentRoute.pathname.includes('/playbill') && <TopNav />}
            <div className={styles.root}>
              <Suspense fallback={<div className="flex-centered">Loading…</div>}>
                <RouteWink>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/join" element={<Join />} />
                  <Route path="/stylesheet" element={<Stylesheet />} />

                  {/* Public event & performer pages */}
                  <Route path="/events" element={<Calendar />} />
                  <Route path="/events/:slug" element={<EventDetail />} />
                  <Route path="/performers" element={<PerformersList />} />
                  <Route path="/performers/:slug" element={<PerformerProfile />} />

                  {/* Logged-in user routes */}
                  <Route element={<RequireAuth />}>
                    <Route path="/performers/new" element={<CreatePerformer />} />
                    <Route path="/performers/:slug/edit" element={<EditPerformer />} />
                  </Route>

                  {/* Admin-only */}
                  <Route element={<RequireAdmin />}>
                    <Route path="/experiences" element={<ExperiencesList />} />
                    <Route path="/create" element={<Create />} />
                    <Route path="/events/new" element={<CreateEvent />} />
                    <Route path="/events/:slug/edit" element={<EditEvent />} />
                  </Route>

                  <Route
                    path="/experiences/:code"
                    element={
                      <ExperienceProvider>
                        <ExperienceAnalytics />
                        <FeedbackExperienceBridge />
                        <Outlet />
                      </ExperienceProvider>
                    }
                  >
                    <Route element={<AllowRegisterRoute />}>
                      <Route path="register" element={<Register />} />
                    </Route>

                    {/* Public/unauthenticated Monitor view */}
                    <Route path="monitor" element={<Monitor />} />

                    <Route element={<RequireExperienceParticipantOrAdmin />}>
                      <Route index element={<Experience />} />
                      <Route path="avatar" element={<Avatar />} />
                      <Route path="playbill" element={<Playbill />} />

                      <Route element={<RequireExperienceHostOrAdmin />}>
                        <Route path="manage" element={<ManageViewer />} />
                        <Route path="manage/focus" element={<FocusMode />} />
                        <Route path="manage/participants" element={<ManageParticipantsPage />} />
                        <Route path="manage/playbill" element={<ManagePlaybillPage />} />
                        <Route path="manage/debug" element={<ManageDebugPage />} />
                        <Route path="manage/blocks/new" element={<ManageCreateBlock />} />
                        <Route path="manage/blocks/:blockId" element={<BlockPage />} />
                        <Route path="timeline" element={<Timeline />} />
                      </Route>
                    </Route>
                  </Route>
                </RouteWink>
              </Suspense>
            </div>
          </div>
        </MotionConfig>
      </FeedbackProvider>
    </UserProvider>
  );
}

export default App;
